/**
 * EPJ Edit - EPLAN EPJ file editing with AI assistance.
 * The EPJ button triggers a file upload directly (no dialog).
 * All conversation and previews happen in the main chat.
 */

import { appendMessage } from "../utils/helpers.js";
import { uploadEpjForEditing, getEpjOverview } from "../services/epjEditService.js";
import { activateEpjMode, deactivateEpjMode, isEpjModeActive } from "../services/chatService.js";
import { getRagFeatures } from "../services/quotaService.js";

export function isEpjEditEnabled() {
    return getRagFeatures().includes('epj');
}

/**
 * Initialize EPJ button — shows/hides based on feature flag,
 * wires up click → file picker → upload → activate EPJ mode.
 */
export function initEpjEditUI() {
    const epjBtn = document.getElementById('epj-btn');
    if (!epjBtn) return;

    if (!isEpjEditEnabled()) {
        epjBtn.classList.add('hidden');
        return;
    }

    epjBtn.classList.remove('hidden');

    const fileInput = document.getElementById('epj-file-input');
    if (!fileInput) return;

    epjBtn.addEventListener('click', () => {
        if (isEpjModeActive()) {
            deactivateEpjMode();
            setEpjButtonState(epjBtn, false);
            appendMessage('EPJ-Modus beendet.');
        } else {
            fileInput.click();
        }
    });

    fileInput.addEventListener('change', (e) => {
        if (e.target.files.length > 0) {
            handleEpjUpload(e.target.files[0]);
            e.target.value = '';
        }
    });
}

function setEpjButtonState(btn, active, fileName = '') {
    if (!btn) return;
    const icon = btn.querySelector('.ms-Icon');
    if (active) {
        btn.classList.add('active');
        btn.title = `EPJ-Modus aktiv: ${fileName} — klicken zum Beenden`;
        if (icon) { icon.className = 'ms-Icon ms-Icon--Cancel'; }
    } else {
        btn.classList.remove('active');
        btn.title = 'EPJ-Datei hochladen und bearbeiten';
        if (icon) { icon.className = 'ms-Icon ms-Icon--Upload'; }
    }
}

async function handleEpjUpload(file) {
    const epjBtn = document.getElementById('epj-btn');
    if (epjBtn) epjBtn.disabled = true;

    // Create a progress message element that we update in place
    const messagesDiv = document.getElementById('chat-messages');
    const progressDiv = document.createElement('div');
    progressDiv.className = 'message assistant-message epj-upload-progress-msg';
    progressDiv.innerHTML =
        `<div class="epj-upload-status">EPJ-Datei "${file.name}" wird hochgeladen...</div>` +
        `<div class="epj-upload-bar-wrap"><div class="epj-upload-bar" style="width:0%"></div></div>`;
    messagesDiv.appendChild(progressDiv);
    messagesDiv.scrollTop = messagesDiv.scrollHeight;

    const statusEl = progressDiv.querySelector('.epj-upload-status');
    const barEl = progressDiv.querySelector('.epj-upload-bar');

    try {
        await uploadEpjForEditing(file, (progress, status) => {
            barEl.style.width = `${progress}%`;
            statusEl.textContent = status || `${progress}%`;
            messagesDiv.scrollTop = messagesDiv.scrollHeight;
        });

        barEl.style.width = '100%';
        statusEl.textContent = 'Analysiere Dateistruktur...';

        const overview = await getEpjOverview(file.name);
        activateEpjMode(file.name, overview, file);

        progressDiv.remove();
        if (epjBtn) epjBtn.disabled = false;
        setEpjButtonState(epjBtn, true, file.name);
        appendMessage(
            `**EPJ-Modus aktiv: ${file.name}**\n\n` +
            `Beschreibe deine Änderungswünsche — z.B. *"Ändere BMK QA10 auf QA11"* oder *"Artikel SIE.5TE8113 zu QA10 hinzufügen"*.\n\n` +
            `EPJ-Schaltfläche erneut drücken zum Beenden.`
        );
    } catch (error) {
        progressDiv.remove();
        if (epjBtn) epjBtn.disabled = false;
        appendMessage(`Fehler beim Hochladen: ${error.message}`);
    }
}
